import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { api } from "@/lib/api";

const SUBJECTS = ["Bespoke Styling", "Press Inquiry", "Concierge Support", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[0], message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message reçu. Nous revenons vers vous sous 48h.");
      setForm({ name: "", email: "", subject: SUBJECTS[0], message: "" });
    } catch {
      toast.error("Envoi impossible. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "h-14 w-full border border-white/10 bg-coal px-5 font-mono text-xs tracking-wider text-zinc-100 placeholder:text-zinc-600 transition-colors duration-300 focus:border-ember focus:outline-none";

  return (
    <div className="mx-auto max-w-[1700px] px-5 pb-28 pt-36 sm:px-8 lg:px-16 lg:pt-44" data-testid="contact-page">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-ember">Contact</p>
            <h1 className="mt-4 font-blackletter text-6xl leading-[1] sm:text-7xl lg:text-8xl">
              Parlez-Nous
            </h1>
            <p className="mt-8 max-w-md font-serif text-xl italic leading-relaxed text-zinc-400">
              Bespoke commissions, press, or concierge support — the studio reads everything.
            </p>
          </Reveal>

          <Reveal delay={0.15} className="mt-14 space-y-8 border-t border-white/5 pt-10">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Atelier</p>
              <p className="mt-2 text-sm text-zinc-300">
                14 Rue de la Fidélité, 75010 Paris
              </p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Email</p>
              <a
                href="mailto:malgrelesdifficultess@gmail.com"
                className="mt-2 block text-sm text-zinc-300 transition-colors duration-300 hover:text-ember"
              >
                malgrelesdifficultess@gmail.com
              </a>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Heures</p>
              <p className="mt-2 text-sm text-zinc-300">Lun – Ven, 10h – 19h CET</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <input
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Nom"
                data-testid="contact-name-input"
                className={inputCls}
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Email"
                data-testid="contact-email-input"
                className={inputCls}
              />
            </div>
            <select
              value={form.subject}
              onChange={set("subject")}
              data-testid="contact-subject-select"
              className={`${inputCls} appearance-none`}
            >
              {SUBJECTS.map((s) => (
                <option key={s} value={s} className="bg-coal">
                  {s}
                </option>
              ))}
            </select>
            <textarea
              required
              value={form.message}
              onChange={set("message")}
              placeholder="Votre message…"
              rows={7}
              data-testid="contact-message-input"
              className="w-full resize-none border border-white/10 bg-coal px-5 py-4 font-mono text-xs tracking-wider text-zinc-100 placeholder:text-zinc-600 transition-colors duration-300 focus:border-ember focus:outline-none"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="group flex h-14 items-center justify-center gap-3 bg-ember px-10 font-mono text-[11px] uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:bg-emberhover disabled:opacity-60"
            >
              {loading ? "Envoi…" : "Envoyer"}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
